/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **The template a source was copied from, as the rows its editors revert to** (Todd, 2026-09-24).
 *
 * Creating from a template is a copy (`TM9a`), and stays one: nothing tracks the template. What a
 * copy does keep is `origin`, and a row the operator changed from the template reads `overridden
 * here` and can be put back to what the template has **now** — *"they may not remember (or have
 * visibility into) what the template used to be."* This is that *now*, read at the moment of the
 * revert and never written back.
 *
 * Read from the templates list `useArtifactLineage` already loads, so it costs no request, and a
 * template the viewer cannot see is simply absent, which is the right answer: nothing is offered.
 * `null` where `origin` names no template (an ordinary Duplicate stamps one too), or none is visible.
 */

import { useMemo } from 'react';

import { useDemandSourceTemplates } from './useDemandSources';
import { headersObjectToRows, paramsDtoToRows, type HeaderRow, type ParameterRow } from '../components/shared';
import { templateToLeaves, type MappingLeaf } from '../components/shared/editors/ortb-body-editor.adapters';

export interface CopiedFromTemplate {
  name: string;
  parameterRows: readonly ParameterRow[];
  headerRows: readonly HeaderRow[];
  bodyLeaves: readonly MappingLeaf[];
}

export function useCopiedFromTemplate(origin: string | null | undefined): CopiedFromTemplate | null {
  const { data: templates } = useDemandSourceTemplates();
  return useMemo(() => {
    const template = origin ? templates?.find((t) => t.objectId === origin) : undefined;
    if (!template) return null;
    return {
      name: template.name,
      parameterRows: paramsDtoToRows(template.params),
      headerRows: headersObjectToRows(template.headers, template.headersProvenance),
      bodyLeaves: templateToLeaves(template.ortbBodyTemplate),
    };
  }, [origin, templates]);
}
